import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Grid,
  TextField,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Paper,
  FormControlLabel,
  FormControl,
  FormLabel,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  InputLabel,
  OutlinedInput,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  Message as MessageIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import TaskFormBase from './shared/TaskFormBase';
import { useAuth } from '../contexts/AuthContext';
import { addMessage, getTaskMessages } from '../services/kanTaskService';

const CreateTaskModal = ({ open, onClose, onSubmit, teamMembers, castMembers }) => {
  const { currentUser } = useAuth();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    phase: 'pre-production',
    department: 'production',
    priority: 'normal',
    startDate: new Date(),
    endDate: new Date(new Date().setDate(new Date().getDate() + 7)),
    location: '',
    progress: 0,
    isMilestone: false,
    assignees: [],
    castMembers: [],
    crewMembers: [], // Added crew members array
    messages: [],
    newMessage: '',
    attachments: [],
    image: null,
    checklist: [],
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = React.useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result;
          setSelectedImage(base64String);
          setImagePreview(base64String);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error processing image:', error);
        setError('Failed to process image. Please try again.');
      }
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setFormData(prev => ({
      ...prev,
      attachments: [...(prev.attachments || []), ...files]
    }));
  };

  const [isMessageLoading, setIsMessageLoading] = useState(false);
  
  const handleMessageSubmit = (message) => {
    if (!message.trim()) return;
    
    // Create a new message object
    const newMessage = {
      sender: currentUser._id,
      senderName: currentUser.name,
      content: message,
      timestamp: new Date()
    };
    
    // Update the form data with the new message
    setFormData(prev => ({
      ...prev,
      messages: [...prev.messages, newMessage],
      newMessage: ''
    }));
  };

  const handleAddChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    
    const newItem = {
      text: newChecklistItem,
      completed: false,
      createdBy: currentUser._id,
      createdAt: new Date()
    };
    
    setFormData(prev => ({
      ...prev,
      checklist: [...(prev.checklist || []), newItem]
    }));
    
    setNewChecklistItem('');
  };
  
  const handleToggleChecklistItem = (index) => {
    setFormData(prev => {
      const updatedChecklist = [...prev.checklist];
      updatedChecklist[index] = {
        ...updatedChecklist[index],
        completed: !updatedChecklist[index].completed,
        completedBy: !updatedChecklist[index].completed ? currentUser._id : null,
        completedAt: !updatedChecklist[index].completed ? new Date() : null
      };
      return {
        ...prev,
        checklist: updatedChecklist
      };
    });
  };
  
  const handleRemoveChecklistItem = (index) => {
    setFormData(prev => {
      const updatedChecklist = prev.checklist.filter((_, i) => i !== index);
      return {
        ...prev,
        checklist: updatedChecklist
      };
    });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');
    
    try {
      if (!formData.title?.trim()) {
        setError('Title is required');
        return;
      }

      // Format dates properly - handle both Date objects and string dates
      const formatDate = (dateValue) => {
        if (!dateValue) return new Date().toISOString();
        if (dateValue instanceof Date) return dateValue.toISOString();
        // If it's a string from the date input (YYYY-MM-DD format)
        if (typeof dateValue === 'string') {
          // Create a new date object from the string
          const date = new Date(dateValue);
          return date.toISOString();
        }
        return new Date().toISOString(); // Fallback
      };

      const taskData = {
        ...formData,
        title: formData.title.trim(),
        description: formData.description?.trim() || '',
        image: selectedImage || null, // Send the base64 string or null
        startDate: formatDate(formData.startDate),
        endDate: formatDate(formData.endDate),
        assignees: formData.assignees || [],
        castMembers: formData.castMembers || [],
        crewMembers: formData.crewMembers || [], // Added crew members
        messages: formData.messages || [],
        attachments: formData.attachments || [],
        checklist: formData.checklist || [], // Include checklist data
      };

      await onSubmit(taskData);
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        phase: 'pre-production',
        department: 'production',
        priority: 'normal',
        startDate: new Date(),
        endDate: new Date(new Date().setDate(new Date().getDate() + 7)),
        location: '',
        progress: 0,
        isMilestone: false,
        assignees: [],
        castMembers: [],
        crewMembers: [], // Added crew members array
        messages: [],
        newMessage: '',
        attachments: [],
        image: null,
      });
      setSelectedImage(null);
      setImagePreview(null);
      setError('');
      onClose();
    } catch (error) {
      console.error('Error creating task:', error);
      setError(error.message || 'Failed to create task. Please try again.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backdropFilter: 'blur(10px)',
          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.85),
          borderRadius: 3,
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: (theme) => `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.1)}`,
          overflow: 'hidden',
          maxHeight: '90vh',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #2196F3, #1976D2)',
            opacity: 0.7
          }
        }
      }}
    >
      <DialogTitle sx={{
        pb: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'linear-gradient(90deg, #2196F3, #1976D2)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        textFillColor: 'transparent',
        fontWeight: 500,
        letterSpacing: '0.5px',
        '&::before': {
          content: '""',
          display: 'inline-block',
          width: '24px',
          height: '24px',
          marginRight: '8px',
          background: `url('data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="#2196F3" d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"/></svg>')}')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          filter: 'drop-shadow(0 0 2px rgba(33, 150, 243, 0.4))'
        }
      }}>
        <span>Create New Task</span>
        <IconButton onClick={onClose} size="small" sx={{ WebkitTextFillColor: 'initial' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              '& .MuiTab-root': { 
                minHeight: '48px',
                py: 1,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
              },
              '& .MuiTab-iconWrapper': {
                mr: 1,
              }
            }}
          >
            <Tab icon={<DescriptionIcon />} label="Details" id="tab-0" aria-controls="tabpanel-0" />
            <Tab icon={<MessageIcon />} label="Messages" id="tab-1" aria-controls="tabpanel-1" />
            <Tab icon={<CheckCircleIcon />} label="Checklist" id="tab-2" aria-controls="tabpanel-2" />
            <Tab icon={<InfoIcon />} label="More Info" id="tab-3" aria-controls="tabpanel-3" />
          </Tabs>
        </Box>

        {/* Error message - shown on all tabs */}
        {error && (
          <Box sx={{
            mx: 2,
            mt: 2,
            p: 1,
            bgcolor: 'error.light',
            color: 'error.dark',
            borderRadius: 1
          }}>
            {error}
          </Box>
        )}

        {/* Tab 0: Details */}
        <Box 
          role="tabpanel"
          hidden={activeTab !== 0}
          id="tabpanel-0"
          aria-labelledby="tab-0"
          sx={{ p: 2 }}
        >
          {activeTab === 0 && (
            <Box component="form" onSubmit={(e) => e.preventDefault()}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    name="title"
                    label="Title"
                    value={formData.title || ''}
                    onChange={handleChange}
                    fullWidth
                    required
                    error={Boolean(error && !formData.title?.trim())}
                    helperText={error && !formData.title?.trim() ? 'Title is required' : ''}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    name="description"
                    label="Description"
                    value={formData.description || ''}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    fullWidth
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                {/* Image Upload Section - Compact Design */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ 
                    mb: 2, 
                    p: 1, 
                    border: '1px dashed #ccc', 
                    borderRadius: 1,
                    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '60px'
                  }}>
                    <input
                      type="file"
                      accept="image/*"
                      id="create-task-image-upload"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          const file = e.target.files[0];
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            const base64String = reader.result;
                            setSelectedImage(base64String);
                            setImagePreview(base64String);
                            setFormData(prev => ({
                              ...prev,
                              image: base64String
                            }));
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    
                    {formData.image ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box sx={{ 
                          width: '40px', 
                          height: '40px', 
                          borderRadius: '4px', 
                          overflow: 'hidden',
                          mr: 1,
                          flexShrink: 0
                        }}>
                          <img 
                            src={typeof formData.image === 'string' ? formData.image : URL.createObjectURL(formData.image)}
                            alt="Preview"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </Box>
                        <Typography variant="body2" noWrap sx={{ flex: 1, fontSize: '0.8rem' }}>
                          {formData.image instanceof File ? formData.image.name : 'Image attached'}
                        </Typography>
                        <IconButton 
                          size="small"
                          onClick={() => {
                            setSelectedImage(null);
                            setImagePreview(null);
                            setFormData(prev => ({ ...prev, image: null }));
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <ImageIcon sx={{ color: 'text.secondary', mr: 1 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                          Add image
                        </Typography>
                        <label htmlFor="create-task-image-upload">
                          <Button
                            variant="outlined"
                            component="span"
                            size="small"
                          >
                            Browse
                          </Button>
                        </label>
                      </Box>
                    )}
                  </Box>
                </Grid>
                
                {/* Date fields in a container to ensure they're side by side */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Date Range</Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      name="startDate"
                      label="Start Date"
                      type="date"
                      value={formData.startDate || ''}
                      onChange={handleChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      name="endDate"
                      label="End Date"
                      type="date"
                      value={formData.endDate || ''}
                      onChange={handleChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={{ mb: 2 }}
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="priority"
                    label="Priority"
                    select
                    value={formData.priority || 'normal'}
                    onChange={handleChange}
                    fullWidth
                    SelectProps={{ native: true }}
                    sx={{ mb: 2 }}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </TextField>
                </Grid>
                

              </Grid>
            </Box>
          )}
        </Box>

        {/* Tab 1: Messages */}
        <Box 
          role="tabpanel"
          hidden={activeTab !== 1}
          id="tabpanel-1"
          aria-labelledby="tab-1"
          sx={{ p: 2 }}
        >
          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <MessageIcon sx={{ mr: 1 }} /> Task Communication
              </Typography>
              
              <Box
                sx={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#2d2d2d' : '#f5f5f5',
                  p: 1,
                  borderRadius: 1,
                  mb: 2
                }}
              >
                {Array.isArray(formData.messages) && formData.messages.length > 0 ? (
                  formData.messages.map((message, index) => (
                    <Box
                      key={`message-${index}`}
                      sx={{
                        mb: 1.5,
                        p: 1.5,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        boxShadow: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {message.senderName || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(message.timestamp).toLocaleTimeString()}
                        </Typography>
                      </Box>
                      <Typography variant="body1">
                        {message.content}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No messages yet. Start a conversation!
                    </Typography>
                  </Box>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Type a message..."
                  value={formData.newMessage || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, newMessage: e.target.value }))}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleMessageSubmit(formData.newMessage);
                    }
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleMessageSubmit(formData.newMessage)}
                  disabled={!formData.newMessage?.trim()}
                  startIcon={<MessageIcon />}
                >
                  Send
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {/* Tab 2: Checklist */}
        <Box 
          role="tabpanel"
          hidden={activeTab !== 2}
          id="tabpanel-2"
          aria-labelledby="tab-2"
          sx={{ p: 2 }}
        >
          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                fontWeight: 600,
                color: theme => theme.palette.primary.main 
              }}>
                <CheckCircleIcon sx={{ mr: 1 }} /> Task Checklist
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Add new checklist item"
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddChecklistItem();
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={handleAddChecklistItem}
                        size="small"
                        color="primary"
                        disabled={!newChecklistItem.trim()}
                      >
                        <AddIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Box>
              
              <List sx={{ 
                maxHeight: '400px', 
                overflow: 'auto',
                bgcolor: theme => theme.palette.mode === 'dark' ? '#2d2d2d' : '#f5f5f5',
                borderRadius: 1,
                p: 1
              }}>
                {formData.checklist && formData.checklist.length > 0 ? (
                  formData.checklist.map((item, index) => (
                    <ListItem 
                      key={`checklist-${index}`}
                      dense
                      sx={{
                        mb: 1,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        boxShadow: 1,
                        '&:hover': {
                          boxShadow: 2
                        }
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={item.completed}
                          onChange={() => handleToggleChecklistItem(index)}
                          icon={<CheckBoxOutlineBlankIcon />}
                          checkedIcon={<CheckBoxIcon />}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          style: item.completed ? { textDecoration: 'line-through', color: '#888' } : {}
                        }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" onClick={() => handleRemoveChecklistItem(index)} size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No checklist items yet. Add some tasks to track progress!
                    </Typography>
                  </Box>
                )}
              </List>
            </Box>
          )}
        </Box>

        {/* Tab 3: More Info */}
        <Box 
          role="tabpanel"
          hidden={activeTab !== 3}
          id="tabpanel-3"
          aria-labelledby="tab-3"
          sx={{ p: 2 }}
        >
          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <InfoIcon sx={{ mr: 1 }} /> Additional Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    name="location"
                    label="Location"
                    value={formData.location || ''}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="department"
                    label="Department"
                    select
                    value={formData.department || 'production'}
                    onChange={handleChange}
                    fullWidth
                    SelectProps={{ native: true }}
                    sx={{ mb: 2 }}
                  >
                    <option value="production">Production</option>
                    <option value="art">Art</option>
                    <option value="camera">Camera</option>
                    <option value="sound">Sound</option>
                    <option value="lighting">Lighting</option>
                    <option value="costume">Costume</option>
                    <option value="makeup">Makeup</option>
                    <option value="editing">Editing</option>
                    <option value="vfx">VFX</option>
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="phase"
                    label="Phase"
                    select
                    value={formData.phase || 'pre-production'}
                    onChange={handleChange}
                    fullWidth
                    SelectProps={{ native: true }}
                    sx={{ mb: 2 }}
                  >
                    <option value="pre-production">Pre-Production</option>
                    <option value="production">Production</option>
                    <option value="post-production">Post-Production</option>
                  </TextField>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isMilestone || false}
                        onChange={(e) => setFormData(prev => ({ ...prev, isMilestone: e.target.checked }))}
                        name="isMilestone"
                        color="primary"
                      />
                    }
                    label="Mark as Milestone"
                  />
                </Grid>
                
                {/* Crew Members Assignment Section */}
                <Grid item xs={12}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="crew-members-label">Assign Crew Members</InputLabel>
                    <Select
                      labelId="crew-members-label"
                      id="crew-members-select"
                      multiple
                      value={formData.crewMembers || []}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          crewMembers: e.target.value
                        }));
                      }}
                      input={<OutlinedInput label="Assign Crew Members" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((member) => (
                            <Chip 
                              key={member._id} 
                              label={member.name || (member.user && member.user.name) || 'Unknown'} 
                              size="small"
                              onDelete={(e) => {
                                e.stopPropagation(); // Prevent dropdown from opening
                                const newCrewMembers = formData.crewMembers.filter(m => 
                                  (m._id !== member._id) && (!m.user || m.user._id !== member._id)
                                );
                                setFormData(prev => ({
                                  ...prev,
                                  crewMembers: newCrewMembers
                                }));
                              }}
                              onMouseDown={(e) => e.stopPropagation()} // Prevent dropdown from opening
                            />
                          ))}
                        </Box>
                      )}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 250
                          },
                        },
                      }}
                    >
                      {teamMembers && teamMembers.length > 0 ? (
                        teamMembers.map((member) => (
                          <MenuItem key={member._id} value={member}>
                            <Checkbox 
                              checked={formData.crewMembers?.some(m => 
                                (m._id === member._id) || (m.user && m.user._id === member._id)
                              )} 
                            />
                            <ListItemText 
                              primary={member.name || (member.user && member.user.name) || 'Unknown'}
                              secondary={member.role || member.department || 'Team Member'}
                            />
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>No team members available</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button 
          onClick={onClose}
          sx={{ 
            borderRadius: 2,
            backdropFilter: 'blur(4px)',
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.5),
            color: (theme) => theme.palette.text.secondary,
            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            '&:hover': {
              backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.7),
              boxShadow: (theme) => `0 4px 12px 0 ${alpha(theme.palette.common.black, 0.08)}`
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          startIcon={<AddIcon />}
          sx={{ 
            borderRadius: 2,
            background: 'linear-gradient(45deg, #2196F3 30%, #1976D2 90%)',
            backdropFilter: 'blur(4px)',
            boxShadow: '0 4px 20px 0 rgba(33, 150, 243, 0.3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #2196F3 10%, #1976D2 70%)',
              boxShadow: '0 6px 25px 0 rgba(33, 150, 243, 0.4)',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease-in-out'
          }}
        >
          Create Task
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTaskModal;
